import { clsx } from "clsx";
import { type FC, type MouseEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHover } from "react-aria";
import { useSelector } from "react-redux";

import {
  Dialog,
  LoadingSpinner,
  Modal,
  TriggerButton,
  TriggerButtonGroup,
  TriggerButtonSize,
  VStack,
  useDialogWithPosition,
} from "widgets";
import { useOverlay, useURLSearchParam } from "widgets/hooks";
import { toSafeInt } from "widgets/utils";

import styleTrack from "../styles/Track.module.scss";

import { DndActiveContext } from "../GenomeViewerInteractiveSurface.js";
import { ShowMessageOverTrack } from "../GenomeViewerTracksList.js";
import { TrackHeader } from "../TrackHeader.js";
import { TrackInteractiveSurface } from "../TrackInteractiveSurface.js";
import { useTrackRefSize } from "../hooks/useTrackSize.js";
import { type TrackRange } from "../new/useViewerGraphql.js";
import { FlagEditor } from "./FlagEditor.js";
import { FlagShape } from "./FlagShape.js";
import { flagShapePileupParams } from "./_mod.js";
import { useGetRegionFlags } from "./useFlagGraphql.js";
import { rangesIntersect } from "@/app-utils/range.ts";
import { type IPileup, type IPileupFlag, PileupFactory } from "@/app-utils/track-shapes/track-pileup-factory.ts";
import type { RegionFlag } from "@/graphql.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { FLAG_HEIGHT } from "@/settings/settings.js";
import { FlagTrackCustomizationDialog } from "@/ui-modules/genome-viewer/flag-track/FlagTrackCustomizationDialog.tsx";

export interface FlagTrackProps {
  range: TrackRange;
  contigId: number;
  headerWidth?: number;
  setRange?: (range: TrackRange) => void;
  setZoomRange?: (range: TrackRange | null) => void;
  setSelectionRange?: (range: TrackRange | null) => void;
}

export const FlagTrack: FC<FlagTrackProps> = (props) => {
  const ref = useRef<HTMLDivElement>(null);
  const { normalize } = useTrackRefSize(ref, props.range);
  const { hoverProps, isHovered } = useHover({});

  const flagId = toSafeInt(useURLSearchParam("flag"));
  const [selectedFlagId, setSelectedFlagId] = useState<string | undefined>(String(flagId));

  const hideFlagSymbol = useSelector<IAppState, boolean>((state) => state.auth.session.params.condensedFlagsDisplay);
  const overallHeight: number = hideFlagSymbol ? 6 : FLAG_HEIGHT;

  const {
    data: { length, flags: regionFlags = [] } = {},
    loading,
    error,
  } = useGetRegionFlags({ contigId: props.contigId });

  const { triggerRef, dialogRef, dialogProps, state, arrowProps, updatePosition } = useDialogWithPosition<
    SVGElement,
    HTMLDivElement
  >({
    onClose() {
      setSelectedFlagId(undefined);
    },
  });
  const { setDndActive } = useContext(DndActiveContext);

  // -- Local Data & State
  const flagsPileUp = useMemo<IPileup | undefined>(() => {
    if (regionFlags) {
      const pileupFactory = new PileupFactory(normalize);
      return pileupFactory.buildFlagPileup(regionFlags as RegionFlag[], props.range, flagShapePileupParams, true);
    }
    return undefined;
  }, [props.range, normalize, regionFlags]);

  // -- Functions & Utils
  const findFlag = (id: string | number) => flagsPileUp?.flags?.find((f) => String(f.flag.id) === String(id));

  const handleClick = useCallback((event: MouseEvent<SVGGElement>, flagPileup: IPileupFlag) => {
    triggerRef.current = event.currentTarget;
    setSelectedFlagId(String(flagPileup.flag.id));
  }, []);

  // Need to come first, before we detect if a flag is already set on the URL
  useEffect(() => {
    state.close();
  }, [props.range]);

  useEffect(() => {
    if (flagId != undefined) {
      // Set flag from url
      setSelectedFlagId(String(flagId));
      triggerRef.current = undefined; // reset for `isInitiallySelected`
    }
  }, [flagId]);

  useEffect(() => {
    state.setOpen(!!triggerRef.current && selectedFlagId != undefined);
  }, [selectedFlagId, state.setOpen]);

  useEffect(() => {
    setDndActive(!state.isOpen);
  }, [setDndActive, state.isOpen]);

  useEffect(() => {
    // The fist Flag Dialog may open while the rendering is ongoing (width being not yet well set)
    // `flagsPileUp` is regenerated with `normalize` changes, itself being connected to the track width.
    if (state.isOpen) updatePosition();
  }, [flagsPileUp]);

  const {
    triggerProps: customizationDialogTriggerProps,
    overlayProps: customizationDialogOverlayProps,
    state: customizationDialogState,
  } = useOverlay({});

  const selectedFlag = findFlag(selectedFlagId)?.flag;

  const interactiveSurfaceHeight = (overallHeight + flagsPileUp.pileupParams.lanePadding) * flagsPileUp.lanes + 10;
  const svgHeight = (overallHeight + flagsPileUp.pileupParams.lanePadding) * flagsPileUp.lanes;

  return (
    <div
      className={clsx(styleTrack.track, styleTrack.track_no_header, { [styleTrack.track_hovered]: isHovered })}
      {...hoverProps}
      data-testid="flag-track"
    >
      <TrackHeader
        isTrackHovered={isHovered}
        title={"Flags"}
        headerWidth={props.headerWidth}
        trackIcons={
          <TriggerButtonGroup vertical>
            <TriggerButton
              {...customizationDialogTriggerProps}
              icon={"settings"}
              tooltip={`Customize flags track`}
              label={"Configure"}
              size={TriggerButtonSize.Small}
            />
            <Modal state={customizationDialogState} {...customizationDialogOverlayProps}>
              <FlagTrackCustomizationDialog onClose={customizationDialogState.close} />
            </Modal>
          </TriggerButtonGroup>
        }
      />
      <TrackInteractiveSurface
        range={props.range}
        contigLength={length}
        setRange={props.setRange}
        setZoomRange={props.setZoomRange}
        setSelectionRange={props.setSelectionRange}
        className={clsx(styleTrack.container, styleTrack.container_no_bottom)}
        ref={ref}
        style={{ height: interactiveSurfaceHeight }}
      >
        {loading ? (
          <LoadingSpinner debounceStart={1000} size={20} />
        ) : (
          <svg height={svgHeight} style={{ width: "100%", pointerEvents: "none" }}>
            {error ? (
              <foreignObject x="0" y="0" width={"100%"} height={overallHeight}>
                <ShowMessageOverTrack message={error.message} />
              </foreignObject>
            ) : (
              flagsPileUp?.flags
                ?.filter((pileupFlag) => {
                  const firstFlag = pileupFlag.flag;
                  return rangesIntersect(firstFlag?.region?.range, props.range);
                })
                .map((pileupFlag, i) => {
                  const isInitiallySelected = /*!alreadyOpen &&*/ String(pileupFlag.flag.id) === String(flagId);
                  return (
                    <g
                      key={`flag-shape-${i}`}
                      ref={(ref) => {
                        if (isInitiallySelected && !!ref && !triggerRef.current) {
                          triggerRef.current = ref;
                          // Open the dialog from here because we can't react from a Ref change
                          state.setOpen(!!ref && selectedFlagId != undefined);
                        }
                      }}
                    >
                      <FlagShape
                        flag={pileupFlag}
                        height={overallHeight}
                        onFlagClick={handleClick}
                        hideFlagSymbol={hideFlagSymbol}
                      />
                    </g>
                  );
                })
            )}
          </svg>
        )}
      </TrackInteractiveSurface>
      {state.isOpen && !loading && (
        <Dialog
          ref={dialogRef}
          domProps={dialogProps}
          arrowProps={arrowProps}
          closeFn={state.close}
          contentStyle={{ padding: 0 }}
        >
          <VStack spacing={0}>
            {selectedFlag && (
              <FlagEditor
                key={`flag-editor-${selectedFlag.id}`}
                flag={selectedFlag}
                onDelete={() => {
                  // when we delete the last one (state not yet updated) we'll unselect the tooltip
                  // as there is none. It will close the Dialog automatically.
                  setSelectedFlagId(undefined);
                }}
              />
            )}
          </VStack>
        </Dialog>
      )}
    </div>
  );
};
